// DTO condivisi BE↔FE per gli inviti utente via email. L'admin sceglie UN
// ruolo e UN recinto per tutto l'invio; l'invitato completa anagrafica e
// password dal link pubblico. Modelliamo SOLO il payload `data` dell'envelope.

import { z } from 'zod';
import { recintoModeSchema, type UserScopePut } from './security';

/** timestamptz: ISO string sul wire, Date lato BE pre-serializzazione. */
const wireTimestamp = z.union([z.string(), z.date()]);

/** Massimo di email per singolo invio. */
export const USER_INVITATION_BATCH_MAX = 100;

/** Validità del link di invito, in giorni. */
export const USER_INVITATION_VALID_DAYS = 7;

export const userInvitationStatusSchema = z.enum(['pending', 'expired']);
export type UserInvitationStatus = z.infer<typeof userInvitationStatusSchema>;

/** Invito aperto (non accettato né revocato), come lo vede l'admin. */
export const userInvitationSchema = z.object({
  id: z.string(),
  batch_id: z.string(),
  email: z.string(),
  role_id: z.string(),
  role_name: z.string().nullable(),
  scope_mode: recintoModeSchema,
  company_ids: z.array(z.string()),
  store_ids: z.array(z.string()),
  status: userInvitationStatusSchema,
  expires_at: wireTimestamp,
  last_sent_at: wireTimestamp,
  send_count: z.number(),
  invited_by_name: z.string().nullable(),
  created_at: wireTimestamp,
});
export type UserInvitation = z.infer<typeof userInvitationSchema>;

export const userInvitationListSchema = z.object({
  invitations: z.array(userInvitationSchema),
});
export type UserInvitationList = z.infer<typeof userInvitationListSchema>;

/** Perché un'email dell'invio non è stata invitata. */
export const userInvitationSkipReasonSchema = z.enum(['already_user', 'duplicate']);
export type UserInvitationSkipReason = z.infer<typeof userInvitationSkipReasonSchema>;

export const userInvitationBatchResultSchema = z.object({
  batch_id: z.string(),
  invited: z.array(z.object({ id: z.string(), email: z.string() })),
  skipped: z.array(z.object({ email: z.string(), reason: userInvitationSkipReasonSchema })),
  /** Inviti creati ma la cui email non è partita: si possono reinviare. */
  email_failed: z.array(z.string()),
});
export type UserInvitationBatchResult = z.infer<typeof userInvitationBatchResultSchema>;

/** Corpo di POST /users/invitations: stesso ruolo e stesso recinto per tutte le email. */
export interface UserInvitationCreateInput {
  emails: string[];
  role_id: string;
  scope: UserScopePut;
}

/** Cosa vede l'invitato aprendo il link, prima di registrarsi. */
export const userInvitationPreviewSchema = z.object({
  email: z.string(),
  tenant_name: z.string(),
  role_name: z.string().nullable(),
  expires_at: wireTimestamp,
});
export type UserInvitationPreview = z.infer<typeof userInvitationPreviewSchema>;

/**
 * Corpo di POST /auth/invitations/accept: gli stessi dati anagrafici del form
 * utente (nome, cognome, cellulare, password); l'email è quella dell'invito.
 */
export interface UserInvitationAcceptInput {
  token: string;
  first_name: string;
  last_name: string;
  mobile?: string;
  password: string;
}

export const userInvitationAcceptResultSchema = z.object({
  tenant_slug: z.string(),
  email: z.string(),
});
export type UserInvitationAcceptResult = z.infer<typeof userInvitationAcceptResultSchema>;
